/*eslint no-magic-numbers: ["error", { "ignore": [0,1,2] }]*/
import React, { useEffect, useState } from 'react'
import { useHandleZip } from '../../../hooks/useHandleZip'
import { useNavigate, useParams } from 'react-router-dom'
import { usePagination } from '../../../hooks/usePagination'
import sortDirection from '../../../constants/sortDirection'
import { getMonitoringSitesForDownload, getMonitoringSitesReportsRequest } from '../../../api/reports'
import qs from 'query-string'
import { showErrorToast } from '../../../utils/toast'
import { buildReportsExpandPath } from '../../../routing/constants'
import ExpandedReportsTable from './ExpandedReportsTable'
import { makeMonitoringSitesTabColumns, normalizeMonitoringSitesData, CSV_HEADER_MAP } from './monitoringTabTable'
import {
  Dash,
  ReportsPaginationWrapper,
  ReportsTableWrapper,
  ReportTableTitle,
  ReportTableWrapper,
  RightButtons
} from '../style'
import { FormattedMessage } from 'react-intl'
import Button from '../../../components/Button'
import Download from '../../../assets/icons/download-icon'
import Expand from '../../../assets/icons/expand-icon'
import Table from '../../../components/Table'
import ItemsPerPageDropdown from '../../../components/ItemsPerPageDropdown'
import Pagination from '../../../features/Pagination'
import { getUrlForAllFilters } from '../index'

const defaultPaginationState = {
  totalNumber: 0,
  resultsPerPage: 10,
  totalPages: 0,
  currentPage: 1,
  hasNext: true,
  hasPrevious: false
}

const defaultFilters = {
  siteFilters: {
    region: [],
    country: [],
    system: [],
    threats: [],
    assessmentStatus: [],
    kbaStatus: [],
    kbaClassification: [],
    kbaCriterias: [],
    dateOfMostRecentAssessment: [],
    url: ''
  },
  speciesFilters: {
    speciesList: [],
    selectedSpecieOptions: []
  },
  ecosystemFilters: {
    ecosystemList: [],
    ecosystemRedListCategory: [],
    url: ''
  },
  ecologicalFilters: {
    ecoregionList: [],
    url: ''
  },
  showReports: false
}

const MonitoringSitesTab = ({
  filters,
  setFilters,
  setLoadingScreenMessage,
  isMonitoringSitesLoading,
  setIsMonitoringSitesLoading,
  isRunReportTriggered
}) => {
  const { downloadZip } = useHandleZip()
  const history = useNavigate()
  const params = useParams()
  const { pagination, setPagination, selectPage, nextPage, prevPage, nextSection, prevSection, changeResultsPerPage } =
    usePagination(defaultPaginationState)
  // const [speciesReports, setSpeciesReports] = useState([{
  //   commonName: '',
  //   iucnStatus: '',
  //   kbaStatus: '',
  //   region: [],
  //   country: [],
  //   redListCrit: null,
  //   restrictedRangeStatus: false,
  //   ecoregionBioregionRestricted: '',
  //   parameter: '',
  //   bestGlobalEstimate: null,
  //   earliestYear: null,
  //   siteEstimate: '',
  //   reproductiveUnits: null,
  //   sourceReproductiveUnits: '',
  //   SpeciesCriteriaTriggered: []
  // }])

  const [monitoringSitesReports, setMonitoringSitesReports] = useState([])
  const [monitoringSitesSorting, setMonitoringSitesSorting] = useState({
    sortBy: 'siteCode' || 'name',
    sortDirection: sortDirection.DESC
  })

  const getMonitoringSitesReports = async () => {
    if (isMonitoringSitesLoading) {
      return
    }
    const MonitoringPagination = {
      resultsPerPage: pagination.resultsPerPage,
      currentPage: pagination.currentPage
    }
    const sorting = {
      sortBy: monitoringSitesSorting.sortBy,
      sortDirection: monitoringSitesSorting.sortDirection
    }
    setIsMonitoringSitesLoading(true)

    try {
      const response = await getMonitoringSitesReportsRequest({
        queryStrings: '',
        pagination: qs.stringify(MonitoringPagination),
        sorting: qs.stringify(sorting)
      })

      if (response.error) {
        setMonitoringSitesReports([])
        setIsMonitoringSitesLoading(false)
        setPagination(defaultPaginationState)
        localStorage.setItem('reportsFilters', JSON.stringify(defaultFilters))
        setFilters(defaultFilters)
        return showErrorToast(response.error, 'showBorder')
      }

      const rawData = response.data || response.monitoringSites || []
      const normalizedData = normalizeMonitoringSitesData(rawData)
      setMonitoringSitesReports(normalizedData)
      if (response.pagination) {
        setPagination(response.pagination)
      }
      setIsMonitoringSitesLoading(false)
    } catch (err) {
      setMonitoringSitesReports([])
      setIsMonitoringSitesLoading(false)
      setPagination(defaultPaginationState)
      localStorage.setItem('reportsFilters', JSON.stringify(defaultFilters))
      setFilters(defaultFilters)
      return showErrorToast('Something went wrong', 'showBorder')
    }
  }

  const expandTable = () => {
    history(buildReportsExpandPath('monitoringSites'))
  }

  useEffect(() => {
    if (filters.showReports) {
      getMonitoringSitesReports()
    }
  }, [
    pagination?.currentPage,
    pagination?.resultsPerPage,
    monitoringSitesSorting?.sortBy,
    monitoringSitesSorting?.sortDirection,
    isRunReportTriggered
  ])

  const downloadCSV = async () => {
    setLoadingScreenMessage('buildingReport')
    setIsMonitoringSitesLoading(true)

    const queryString = getUrlForAllFilters(filters)
    const sorting = qs.stringify({
      sortBy: monitoringSitesSorting.sortBy,
      sortDirection: monitoringSitesSorting.sortDirection
    })

    try {
      const response = await getMonitoringSitesForDownload(queryString, sorting)
      if (response.error) {
        showErrorToast('something go wrong')
        setLoadingScreenMessage('runningReport')
        return setIsMonitoringSitesLoading(false)
      }

      const rawData = JSON.parse(response.join('')).flat()
      const normalizedData = normalizeMonitoringSitesData(rawData)

      const formattedData = normalizedData.map((item) => {
        const row = {}
        Object.keys(CSV_HEADER_MAP).forEach((key) => {
          row[CSV_HEADER_MAP[key]] = item[key]
        })
        return row
      })

      setIsMonitoringSitesLoading(false)
      setLoadingScreenMessage('runningReport')
      return downloadZip(formattedData, 'Monitoring_Sites_report_')
    } catch (err) {
      setLoadingScreenMessage('runningReport')
      setIsMonitoringSitesLoading(false)
      showErrorToast('Something went wrong')
    }
  }

  if (params.title && monitoringSitesReports.length !== 0) {
    return (
      <ExpandedReportsTable
        title={params.title}
        loading={isMonitoringSitesLoading}
        rows={monitoringSitesReports}
        columns={makeMonitoringSitesTabColumns(
          monitoringSitesSorting,
          setMonitoringSitesSorting,
          monitoringSitesReports
        )}
        totalNumber={pagination.totalNumber}
        resultsPerPage={pagination.resultsPerPage}
        changeResultsPerPage={changeResultsPerPage}
        download={downloadCSV}
      />
    )
  }

  return (
    <div>
      <ReportTableWrapper>
        <ReportTableTitle>
          <FormattedMessage id="Monitoring sites" />
          <Dash>{'—'}</Dash>
          <FormattedMessage id="reports" />
        </ReportTableTitle>
        <RightButtons>
          <Button dataTest="download" withIcon={true} height="32px" marginTop="0px" onClick={downloadCSV}>
            <Download />
            <FormattedMessage id="download" />
          </Button>
          <Button dataTest="expand" withIcon={true} height="32px" marginTop="0px" onClick={expandTable}>
            <Expand />
            <FormattedMessage id="expand" />
          </Button>
        </RightButtons>
      </ReportTableWrapper>
      <ReportsTableWrapper activeTab="siteDetails">
        <Table
          columns={makeMonitoringSitesTabColumns(
            monitoringSitesSorting,
            setMonitoringSitesSorting,
            monitoringSitesReports
          )}
          rows={monitoringSitesReports}
          noClearFilter={true}
          noResultMessage="noSpeciesMatchFilteringCriteria"
          dataTest="monitoringSitesTab-list"
          isLoading={isMonitoringSitesLoading}
        />
      </ReportsTableWrapper>
      <ReportsPaginationWrapper>
        <ItemsPerPageDropdown
          currentPage={pagination?.currentPage}
          resultsPerPage={pagination?.resultsPerPage}
          totalNumber={pagination?.totalNumber}
          onChange={changeResultsPerPage}
          dataTest="species-items-per-page"
          menuPlacement="top"
        />
        {pagination?.totalPages > 1 && (
          <Pagination
            activePages={pagination?.currentPage}
            numberOfPages={pagination?.totalPages}
            nextPage={nextPage}
            prevPage={prevPage}
            nextSection={nextSection}
            prevSection={prevSection}
            selectPage={selectPage}
            dataTest="page-number"
          />
        )}
      </ReportsPaginationWrapper>
    </div>
  )
}

export default MonitoringSitesTab
